import * as AppConstants from 'app/AppConstants';
import {concat, difference} from 'utilities/Set';
import keyBy from 'lodash/keyBy';
import constantObject from 'utilities/constantObject';
import {randomHash} from 'utilities/hash';

const initialState = () => ({
  filters: {},
  filtersByCardId: {},
  segmentsByCardId: {},
  filterGroupsByCardId: {},
  stateByCardId: {},
  dateGrainByCardId: {},
  dateRangeByCardId: {},
  overrideDateRangeByCardId: {},
  functionOverridesByCardId: {},
  dataControlFiltersByCardId: {},
  hasQueryStringErrorById: {},
});

export const STATES = constantObject(['CLEARED', 'LOADING', 'LOADED', 'ERROR']);

const addFilters = (state, cardId, filters) => {
  const appliedFilters = filters.map((f) => ({...f, applied: true}));
  const keyedFilters = keyBy(appliedFilters, 'id');
  const filterIds = Object.keys(keyedFilters);

  return {
    ...state,
    filters: {
      ...state.filters,
      ...keyedFilters,
    },
    filtersByCardId: {
      ...state.filtersByCardId,
      [cardId]: concat(state.filtersByCardId[cardId], filterIds),
    },
    stateByCardId: {
      ...state.stateByCardId,
      [cardId]: STATES.LOADED,
    },
  };
};

const addIdToFilter = (filter) => ({
  ...filter,
  id: randomHash(),
});

export default (state = initialState(), action) => {
  switch (action.type) {
    case AppConstants.CARD__LOADED_QUERYSTRING_CARD_FILTERS: {
      const {cardId, filters} = action.payload;
      const filtersWithIds = filters.map((f) => addIdToFilter(f));
      return addFilters(state, cardId, filtersWithIds);
    }

    case AppConstants.CARD__LOADED_QUERYSTRING_CARD_FILTERS_FAILED: {
      const {cardId} = action.payload;
      return {
        ...state,
        hasQueryStringErrorById: {
          [cardId]: true,
        },
        stateByCardId: {
          ...state.stateByCardId,
          [cardId]: STATES.ERROR,
        },
      };
    }

    case AppConstants.CARD__ADDED_FILTER: {
      const {cardId, filter} = action.payload;

      return {
        ...state,
        filters: {
          ...state.filters,
          [filter.id]: filter,
        },
        filtersByCardId: {
          ...state.filtersByCardId,
          [cardId]: concat([filter.id], state.filtersByCardId[cardId]),
        },
      };
    }

    case AppConstants.CARD__REMOVED_FILTER: {
      const {cardId, filterId} = action.payload;
      const filters = {...state.filters};
      delete filters[filterId];

      return {
        ...state,
        filters,
        filtersByCardId: {
          ...state.filtersByCardId,
          [cardId]: difference(state.filtersByCardId[cardId], [filterId]),
        },
      };
    }

    case AppConstants.CARD__APPLIED_FILTER: {
      const {filter} = action.payload;

      return {
        ...state,
        filters: {
          ...state.filters,
          [filter.id]: {
            ...filter,
            applied: true,
          },
        },
      };
    }

    case AppConstants.CARD__APPLIED_FILTER_GROUPS: {
      const {cardId, filterGroupIds} = action.payload;

      return {
        ...state,
        filterGroupsByCardId: {
          ...state.filterGroupsByCardId,
          [cardId]: filterGroupIds,
        },
      };
    }

    case AppConstants.CARD__LOAD_STATE_FROM_VIEW_STARTED:
    case AppConstants.CARD__LOADED_QUERYSTRING_CARD_FILTERS_STARTED:
    case AppConstants.CARD__COPY_FILTERS_FROM_PAGE_STARTED: {
      const {cardId} = action.payload;

      return {
        ...state,
        stateByCardId: {
          ...state.stateByCardId,
          [cardId]: STATES.LOADING,
        },
      };
    }

    case AppConstants.CARD__LOAD_STATE_FROM_VIEW_FINISHED: {
      const {
        cardId,
        filters,
        segments,
        filterGroupIds,
        dateRange,
        dateGrain,
        overrideDateRange,
        functionOverrides,
        dataControlFilters,
      } = action.payload;
      return {
        ...addFilters(state, cardId, filters),
        filterGroupsByCardId: {
          ...state.filterGroupsByCardId,
          [cardId]: filterGroupIds,
        },
        segmentsByCardId: {
          ...state.segmentsByCardId,
          [cardId]: segments,
        },
        dateRangeByCardId: {
          ...state.dateRangeByCardId,
          [cardId]: dateRange,
        },
        dateGrainByCardId: {
          ...state.dateGrainByCardId,
          [cardId]: dateGrain,
        },
        overrideDateRangeByCardId: {
          ...state.overrideDateRangeByCardId,
          [cardId]: overrideDateRange,
        },
        functionOverridesByCardId: {
          ...state.functionOverridesByCardId,
          [cardId]: functionOverrides,
        },
        dataControlFiltersByCardId: {
          ...state.dataControlFiltersByCardId,
          [cardId]: dataControlFilters,
        },
      };
    }

    case AppConstants.CARD__COPY_FILTERS_FROM_PAGE_FINISHED: {
      const {cardId, filters} = action.payload;
      return addFilters(state, cardId, filters);
    }

    case AppConstants.CARD__DATE_RANGE_UPDATED: {
      const {dateRange, cardId} = action.payload;

      return {
        ...state,
        dateRangeByCardId: {
          ...state.dateRangeByCardId,
          [cardId]: dateRange,
        },
      };
    }

    case AppConstants.CARD__DATE_GRAIN_UPDATED: {
      const {dateGrain, cardId} = action.payload;

      return {
        ...state,
        dateGrainByCardId: {
          ...state.dateGrainByCardId,
          [cardId]: dateGrain,
        },
      };
    }

    case AppConstants.CARD__RESET_STATE: {
      const {cardId} = action.payload;
      const filtersByCardId = {...state.filtersByCardId};
      delete filtersByCardId[cardId];
      const segmentsByCardId = {...state.segmentsByCardId};
      delete segmentsByCardId[cardId];
      const filterGroupsByCardId = {...state.filterGroupsByCardId};
      delete filterGroupsByCardId[cardId];
      const dateRangeByCardId = {...state.dateRangeByCardId};
      delete dateRangeByCardId[cardId];
      const dateGrainByCardId = {...state.dateGrainByCardId};
      delete dateGrainByCardId[cardId];
      const overrideDateRangeByCardId = {...state.overrideDateRangeByCardId};
      delete overrideDateRangeByCardId[cardId];
      const functionOverridesByCardId = {...state.functionOverridesByCardId};
      delete functionOverridesByCardId[cardId];

      return {
        ...state,
        filtersByCardId,
        segmentsByCardId,
        filterGroupsByCardId,
        dateGrainByCardId,
        dateRangeByCardId,
        overrideDateRangeByCardId,
        functionOverridesByCardId,
        stateByCardId: {
          ...state.stateByCardId,
          [cardId]: STATES.CLEARED,
        },
      };
    }

    case AppConstants.RPC__FILTERS_APPLIED: {
      const {cardId, filters} = action.payload;
      const filtersWithIds = filters.map(addIdToFilter);
      return addFilters(
        {
          ...state,
          filtersByCardId: {
            ...state.filtersByCardId,
            // clear out existing filters
            [cardId]: null,
          },
        },
        cardId,
        filtersWithIds
      );
    }

    case AppConstants.CARD__CLEARED_QUERYSTRING_ERROR: {
      return {
        ...state,
        hasQueryStringErrorById: {
          ...state.hasQueryStringErrorById,
          [action.payload.cardId]: false,
        },
      };
    }

    default:
      return state;
  }
};
